import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios';
import qs from 'qs';
import { Logger } from '../../lib/logger';
import { Account } from '@app/api';

interface SaveAccountAction {
  type: 'AUTH.SAVE_ACCOUNT';
  payload: Account;
}

export interface LogoutAction {
  type: 'AUTH.LOGOUT';
  payload?: string[];
}

type Action = LogoutAction | SaveAccountAction;
export type AuthAction = Action;

export const saveAccount = (account: Account): SaveAccountAction => ({
  type: 'AUTH.SAVE_ACCOUNT',
  payload: account,
});

const createLogoutAction = (keptEntitiesName?: string[]): LogoutAction => ({
  type: 'AUTH.LOGOUT',
  payload: keptEntitiesName,
});

export const logout =
  (keptEntitiesName?: string[]) => async (dispatch: Function) => {
    await dispatch(createLogoutAction(keptEntitiesName));
  };

const warnError = (e: AxiosError) => {
  if (!e.response) {
    Logger.apiFatal(e);
    return;
  }

  const data = e.response.data as any;
  const code = data ? (data.code ? data.code : 'no-code') : 'no-data';
  if (e.response.status === 400 && code === 'VALIDATION_FAILED') {
    return;
  }
  Logger.warnError(e, { url: e.config?.url }, { type: 'api', code });
};

export const fetchWithAuth =
  (options: AxiosRequestConfig, _noLogout?: boolean) =>
  async (): Promise<AxiosResponse> => {
    try {
      const headers = { ...options.headers };
      const result = await axios({
        ...options,
        headers,
        withCredentials: true,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: 'repeat' }),
      });
      return result;
    } catch (e) {
      warnError(e as AxiosError);
      throw e;
    }
  };

// REDUCER

interface State {
  account: string | undefined;
  connectedAsOther: boolean;
  roles: string[];
}

const initialState: State = {
  account: undefined,
  connectedAsOther: false,
  roles: [],
};

function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'AUTH.SAVE_ACCOUNT':
      return {
        ...state,
        account: action.payload.id,
        roles: action.payload.roles,
        connectedAsOther: !!action.payload.loggedAsOther,
      };
    case 'AUTH.LOGOUT':
      return initialState;
    default:
      return state;
  }
}

export const authReducer = reducer;

// SELECTORS

export const AUTH_MODULE_KEY: 'auth' = 'auth';
export type AuthGlobalState = { [AUTH_MODULE_KEY]: State };
type GlobalState = AuthGlobalState;

export const isLoggedSelector = (state: GlobalState): boolean =>
  !!state[AUTH_MODULE_KEY].account;

export const isConnectedAsOtherSelector = (state: GlobalState): boolean =>
  state[AUTH_MODULE_KEY].connectedAsOther;

export const currentAccountIdSelector = (
  state: GlobalState,
): string | undefined => state[AUTH_MODULE_KEY].account;

export const currentAccountRolesSelector = (state: GlobalState): string[] =>
  state[AUTH_MODULE_KEY].roles;

export const createIsLoggedWithRoleSelector =
  (role: string) =>
  (state: GlobalState): boolean => {
    const roles = currentAccountRolesSelector(state);
    return roles && roles.includes(role);
  };

export const isBetaSelector = (state: any) => {
  return (
    createIsLoggedWithRoleSelector('BETA')(state) ||
    isConnectedAsOtherSelector(state)
  );
};
export const isAdminSelector = (state: any) =>
  createIsLoggedWithRoleSelector('ADMIN')(state) ||
  isConnectedAsOtherSelector(state);
